import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useEditor, EditorContent, Extension } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ListItem from "@tiptap/extension-list-item";
import TableBlock from "./extensions/TableBlock"; // 👈 replaced @tiptap/extension-table
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import DOMPurify from "dompurify";
import Toolbar from "./Toolbar";
import Label from "./extensions/Label";
import ContentLink from "./extensions/ContentLink";
import { Accordion, AccordionItem, AccordionTitle, AccordionContent } from "./extensions/Accordion";
import removeUnderlineStyles from "../utils/removeUnderlineStyles";
import ContentLinkModal from "./modals/ContentLinkModal";
import ModelContentLink from "../../../modal/ModelContentLink";
import ImageBlock from "./extensions/ImageBlock";
import ImageLink from "./extensions/ImageLink";
import ImageLinkModal from "./modals/ImageLinkModal";
import axios from "axios";
import '../../../styles/editor.css';
import useAuth from '../../../hooks/useAuth'
import { addAccordion, addList, addRef, getActiveLinkNode, insertImageList, insertLink, insertVideoList, isBlockActive, isLinkActive, isMarkActive, isModelLinkActive, toggleMark, unwrapLink, unwrapModelLink, getActiveMarkerNode, insertMarkerLeaf, insertContentLink, getActiveContentLinkNode, unwrapContentLink, isContentLinkActive, isImageLinkActive, unwrapImageLink, insertTable, usePopup } from '../../editor-common/helpers';
import level1Data from "../../../data/level1.json";
import level2Data from "../../../data/level2.json";
import level3Data from "../../../data/level3.json";
import level4Data from "../../../data/level4.json";


const CustomListItem = ListItem.extend({
    content: "(paragraph | heading) block*",
});

const CustomOrderedList = OrderedList.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            type: {
                default: "1",
                parseHTML: (element) => element.getAttribute("type") || "1",
                renderHTML: (attributes) => {
                    return {
                        type: attributes.type,
                        class: `list-type-${attributes.type || '1'}`,
                    };
                },
            },
        };
    },

});

const ListCommands = Extension.create({
    name: 'listCommands',
    addCommands() {
        return {
            setListType: (type) => ({ chain, state, commands }) => {
                const { selection } = state;
                const { $from } = selection;

                // Find the closest list ancestor (bulletList OR orderedList)
                let pos = null;
                let closestListNode = null;
                for (let i = $from.depth; i > 0; i--) {
                    const node = $from.node(i);
                    if (node.type.name === 'orderedList' || node.type.name === 'bulletList') {
                        pos = $from.before(i);
                        closestListNode = node;
                        break;
                    }
                }

                if (pos !== null && closestListNode) {
                    return commands.command(({ tr }) => {
                        const node = tr.doc.nodeAt(pos);
                        if (!node) return false;

                        if (node.type.name === 'orderedList') {
                            // Already an ordered list — just update the type attribute
                            tr.setNodeMarkup(pos, null, { ...node.attrs, type });
                            return true;
                        }

                        if (node.type.name === 'bulletList') {
                            // Convert bulletList → orderedList with the desired type
                            const orderedListType = state.schema.nodes.orderedList;
                            tr.setNodeMarkup(pos, orderedListType, { type });
                            return true;
                        }

                        return false;
                    });
                }

                // Fallback: if not in a list, toggle it and THEN update attributes
                return chain()
                    .toggleOrderedList()
                    .updateAttributes('orderedList', { type })
                    .run();
            },
        };
    },
});

const Editor = () => {
    const { level1, level2, level3, level4, level5 } = useParams();

    const [showContentModal, setShowContentModal] = useState(false);
    const [showImageLinkModal, setShowImageLinkModal] = useState(false);
    const [contentLink, setContentLink] = useState(null);
    const [selectedLink, setSelectedLink] = useState(null);
    const [savingContent, setSavingContent] = useState(false);
    const [contentSelectionRange, setContentSelectionRange] = useState("");
    const [loadingContent, setLoadingContent] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [annots, setAnnots] = useState([]);
    const { user } = useAuth();
    const handleSaveContentLink = () => {
        insertContentLink(editor, selectedLink);
    };
    // console.log("level1Data",level1Data[level1]);
    // let name = level1 && level1Data[level1][0].name + "/" + level2Data[`${level1}/${level2}`].name + "/" + level3Data[`${level1}/${level2}/${level3}`].name + "/" + level4Data[`${level1}/${level2}/${level3}/${level4}`].name;
    const name = [
                level1Data?.[level1]?.[0]?.name,
                level2Data?.[`${level1}/${level2}`]?.name,
                level3Data?.[`${level1}/${level2}/${level3}`]?.name,
                level4Data?.[`${level1}/${level2}/${level3}/${level4}`]?.name,
                ].filter(Boolean).join("/");

    const editor = useEditor({

        extensions: [
            StarterKit.configure({
                listItem: false,
                orderedList: false, // Disable default
                bulletList: false,  // Disable default to ensure clean override
                link: {
                    openOnClick: false,
                },
            }),
            CustomListItem,
            CustomOrderedList,
            BulletList,
            ImageBlock,
            TableBlock.configure({
                resizable: false,
                HTMLAttributes: {
                    class: "custom-table",
                    style: "width: 100%; table-layout: fixed;",
                },
            }),
            TableRow,
            TableHeader,
            TableCell,
            Label,
            ContentLink,
            Accordion,
            AccordionItem,
            AccordionTitle,
            AccordionContent,
            ImageLink,
            ListCommands,
        ],
        content: "",


        editorProps: {
            transformPastedHTML(html) {
                const isOffice = /mso-|MsoListParagraph|docs-internal-guid/i.test(html);
                if (!isOffice) return DOMPurify.sanitize(html);

                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                const fragment = document.createDocumentFragment();
                let listStack = []; // Level track karne ke liye stack

                // Saare nodes ko iterate karein
                Array.from(doc.body.childNodes).forEach(node => {
                    const isListPara = node.nodeType === 1 &&
                        (node.classList.contains('MsoListParagraph') ||
                            node.getAttribute('style')?.includes('mso-list'));

                    if (isListPara) {
                        // 1. Level Detect karein (Word level0, level1 bhejta hai)
                        const style = node.getAttribute('style') || '';
                        const levelMatch = style.match(/level(\d+)/);
                        const level = levelMatch ? parseInt(levelMatch[1], 10) : 0;

                        // 2. Fake bullets saaf karein
                        const ignoreTags = node.querySelectorAll('[style*="mso-list:Ignore"]');
                        ignoreTags.forEach(tag => tag.remove());

                        const li = document.createElement('li');
                        li.innerHTML = node.innerHTML;

                        // 3. Nesting Logic (Stack Management)
                        if (level > listStack.length - 1) {
                            // Deep nesting mein ja rahe hain -> Naya <ul> pichle <li> ke andar
                            while (level > listStack.length - 1) {
                                const newUl = document.createElement('ul');
                                if (listStack.length > 0) {
                                    const lastLi = listStack[listStack.length - 1].lastElementChild;
                                    if (lastLi) lastLi.appendChild(newUl);
                                    else listStack[listStack.length - 1].appendChild(newUl);
                                } else {
                                    fragment.appendChild(newUl);
                                }
                                listStack.push(newUl);
                            }
                        } else if (level < listStack.length - 1) {
                            // Bahar aa rahe hain -> Stack se <ul> nikalein
                            while (level < listStack.length - 1) {
                                listStack.pop();
                            }
                        }

                        // Sahi level wale <ul> mein item add karein
                        if (listStack.length > 0) {
                            listStack[listStack.length - 1].appendChild(li);
                        } else {
                            const fallbackUl = document.createElement('ul');
                            fallbackUl.appendChild(li);
                            fragment.appendChild(fallbackUl);
                            listStack.push(fallbackUl);
                        }
                    } else {
                        // Normal paragraph hai: list stack reset karein aur append karein
                        listStack = [];
                        fragment.appendChild(node.cloneNode(true));
                    }
                });

                const tempDiv = document.createElement('div');
                tempDiv.appendChild(fragment);

                let clean = DOMPurify.sanitize(tempDiv.innerHTML, {
                    USE_PROFILES: { html: true },
                    ALLOWED_ATTR: ["href", "src", "style", "class"],
                });

                return removeUnderlineStyles(clean);
            },
        },
    });

    const fetchContent = async (url) => {
        try {
            const payload = {
                level1,
                level2,
                level3,
                level4,
                level5: level5 || '',
            };

            const token = user.jwtToken;

            const response = await axios.post(url, payload, {
                headers: { Authorization: "Bearer " + token },
            });

            let jsonData = null;
            if (response.data.json_content) {
                try {
                    jsonData = typeof response.data.json_content === 'string'
                        ? JSON.parse(response.data.json_content)
                        : response.data.json_content;
                } catch (e) {
                    console.error("Editor: Error parsing json_content:", e);
                    jsonData = null;
                }
            }

            // fallback to HTML if needed
            if (jsonData) {
                // Sanitize JSON to remove any nodes missing a type, which causes "Unknown node type: undefined"
                const sanitizeContent = (node) => {
                    if (!node || typeof node !== 'object') return null;
                    // Tiptap nodes must have a valid string type
                    if (!node.type || node.type === 'undefined') {
                        console.warn("Removed invalid node missing type:", node);
                        return null;
                    }
                    if (node.content && Array.isArray(node.content)) {
                        node.content = node.content.map(sanitizeContent).filter(Boolean);
                    }
                    if (node.marks && Array.isArray(node.marks)) {
                        // Some old data might have invalid marks
                        node.marks = node.marks.filter(m => m && m.type);
                    }
                    return node;
                };
                jsonData = sanitizeContent(jsonData);

                const normalizeOldAccordionTypes = (node) => {
                    if (!node || typeof node !== 'object') return node;

                    const typeMap = {
                        'accordion-item': 'accordionItem',
                        'accordion-title': 'accordionTitle',
                        'accordion-content': 'accordionContent',
                    };

                    const normalized = { ...node, type: typeMap[node.type] || node.type };

                    if (Array.isArray(node.children) && !Array.isArray(node.content)) {
                        normalized.content = node.children.map(normalizeOldAccordionTypes).filter(Boolean);
                        delete normalized.children;
                    }

                    if (Array.isArray(node.content)) {
                        normalized.content = node.content.map(normalizeOldAccordionTypes).filter(Boolean);
                    }

                    if ((normalized.type === 'accordionTitle' || normalized.type === 'accordionContent') && Array.isArray(normalized.content)) {
                        const first = normalized.content[0];
                        if (first && (first.type === 'text' || first.type === undefined)) {
                            normalized.content = [{ type: 'paragraph', content: normalized.content }];
                        }
                    }

                    if (Array.isArray(node.marks)) {
                        normalized.marks = node.marks;
                    }

                    return normalized;
                };

                jsonData = normalizeOldAccordionTypes(jsonData);
                editor.commands.setContent(jsonData);
            } else {
                editor.commands.setContent("<p></p>");
            }
            loadImages();

        } catch (err) {
            console.error(err);
        } finally {
            setLoadingContent(false);
        }
    };

    const loadImages = async () => {
        if (!editor) return;

        try {
            const json = editor.getJSON();

            const imageIds = [];

            const traverse = (node) => {
                if (!node) return;

                // ✅ detect imageBlock
                if (node.type === "imageBlock" && node.attrs?.images) {
                    node.attrs.images.forEach(img => {
                        if (img.id) {
                            imageIds.push(img.id);
                        }
                    });
                }

                // ✅ recursive traversal
                if (node.content) {
                    node.content.forEach(traverse);
                }
            };

            traverse(json);

            // remove duplicates
            const uniqueIds = [...new Set(imageIds)];

            if (!uniqueIds.length) return;

            const URL = process.env.REACT_APP_API_URL + `/get-content-images`;
            const token = user.jwtToken;

            const res = await axios.post(
                URL,
                { imageIds: uniqueIds },
                {
                    headers: { Authorization: "Bearer " + token }
                }
            );

            // 👇 OPTIONAL: map back into editor if needed
            const imageMap = {};
            res.data.forEach(img => {
                imageMap[img._id] = img;
            });

            // 🔥 update editor JSON with latest image data
            const updateImages = (node) => {
                if (!node) return;
                if (node.type === "imageBlock" && node.attrs?.images) {
                    node.attrs.images = node.attrs.images.map(img => {
                        if (imageMap[img.id]) {
                            return {
                                ...img,
                                url: imageMap[img.id]?.url || img.url || "",
                                label: imageMap[img.id]?.label || img.label || "",
                                description: imageMap[img.id]?.description || img.description || "",
                            };
                        }
                        return img;
                    });
                }

                if (node.content && Array.isArray(node.content)) {
                    node.content.forEach(updateImages);
                }
            };

            updateImages(json);

            // 🔥 IMPORTANT: re-set content
            editor.commands.setContent(json);

        } catch (err) {
            console.error(err);
        }
    };

    const fetchLabel = async () => {
        try {
            const URL = process.env.REACT_APP_API_URL + "/get-labels-by-model";

            const token = user.jwtToken;

            const response = await axios.post(
                URL,
                { model: level2 },
                { headers: { Authorization: "Bearer " + token } }
            );

            setAnnots(response.data);

        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!editor) return;

        const url = process.env.REACT_APP_API_URL + "/get-slate-content-in-edit";

        fetchContent(url);

    }, [editor, level1, level2, level3, level4, level5]);

    useEffect(() => {
        fetchLabel();
    }, []);



    const saveContent = async () => {
        if (!editor) return;

        try {
            setSavingContent(true);

            const json = editor.getJSON();   // structured content

            const payload = {
                level1,
                level2,
                level3,
                level4,
                level5: level5 || '',
                json_content: JSON.stringify(json),
            };

            const URL = process.env.REACT_APP_API_URL + "/insert-update-slate-content";

            const token = user.jwtToken;

            await axios.post(URL, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

        } catch (err) {
            console.error(err);
        } finally {
            setSavingContent(false);
        }
    };


    return (
        <>
            {/*showModal && (
                <ModelContentLink
                    setContentLink={setContentLink}
                    contentLink={selectedLink}
                    contentSelectionRange={contentSelectionRange}
                    handleSaveContentLink={handleSaveContentLink}
                    modal={showModal}
                    setModal={setShowModal}
              />
            )*/}

            {showModal && (
                <ContentLinkModal
                    editor={editor}
                    modal={showModal}
                    onClose={() => setShowModal(false)}
                />
            )}

            {showImageLinkModal && (
                <ImageLinkModal
                    editor={editor}
                    onClose={() => setShowImageLinkModal(false)}
                />
            )}
            <div className="editor-wrapper">
                <div className="editor-sticky-header">
                    {/* ✅ STYLIZED HEADER */}
                    <div className="editor-sticky-header-row1">
                        <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600, color: "var(--text-main)" }}>
                            {(loadingContent) ? "Loading..." : `Edit: ${name}`}
                        </h2>
                        <div>
                            <button
                                className="btn btn-primary"
                                onClick={saveContent}
                                disabled={savingContent}
                                style={{ minWidth: "100px" }}
                            >
                                {savingContent ? (
                                    <>
                                        <span className="spinner"></span> Saving...
                                    </>
                                ) : "Save"}
                            </button>
                            <Link className="drawer-close text-decoration-none ms-3 text-lt-gray-800 mt-2" to={`/${level1}/${level2}/${level3}/${level4}`}>
                                <img src="/images/closeblack.png" alt="" style={{ width: "2rem" }} />
                            </Link>
                        </div>
                    </div>

                    <Toolbar
                        editor={editor}
                        onContentLinkClick={() => setShowModal(true)}
                        onImageLinkClick={() => setShowImageLinkModal(true)}
                    />
                </div>

                <div className="editor-content-area">
                    <EditorContent editor={editor} />
                </div>
            </div>
            <div className="pt-3 row d-flex justify-content-center" style={{ maxWidth: "1400px", margin: "2rem auto" }}>
                {/* {level4Data[`${level1}/${level2}/${level3}/${level4}`] &&
                    level4Data[`${level1}/${level2}/${level3}/${level4}`]
                        .nav ? (
                    <>
                        <div className="col-12 col-xl-6 mb-2 d-flex justify-content-center justify-content-xl-start">
                            <Link
                                className="btn btn-green1 btn-sm fw-bold"
                                to={`/edit/${level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                ].nav[0]
                                    }`}
                            >
                                <span>
                                    <span className="icon-left fs-6"></span>
                                    {level4Data[
                                        level4Data[
                                            `${level1}/${level2}/${level3}/${level4}`
                                        ].nav[0]
                                    ]
                                        ? `${level4Data[
                                            level4Data[
                                                `${level1}/${level2}/${level3}/${level4}`
                                            ].nav[0]
                                        ].version
                                        } ${level4Data[
                                            level4Data[
                                                `${level1}/${level2}/${level3}/${level4}`
                                            ].nav[0]
                                        ].name
                                        }`
                                        : ""}
                                </span>
                            </Link>
                        </div>
                        <div className="col-12 col-xl-6 mb-2 d-flex justify-content-center justify-content-xl-end">
                            <Link
                                className="btn btn-green1 btn-sm fw-bold"
                                to={`/edit/${level4Data[
                                    `${level1}/${level2}/${level3}/${level4}`
                                ].nav[1]
                                    }`}
                            >
                                <span>
                                    {level4Data[
                                        level4Data[
                                            `${level1}/${level2}/${level3}/${level4}`
                                        ].nav[1]
                                    ]
                                        ? `${level4Data[
                                            level4Data[
                                                `${level1}/${level2}/${level3}/${level4}`
                                            ].nav[1]
                                        ].version
                                        } ${level4Data[
                                            level4Data[
                                                `${level1}/${level2}/${level3}/${level4}`
                                            ].nav[1]
                                        ].name
                                        }`
                                        : ""}
                                    <span className="icon-right fs-6"></span>
                                </span>
                            </Link>
                        </div>
                    </>
                ) : (
                    ""
                )} */}
            </div>
        </>
    );
};

export default Editor;